import styled from '@emotion/styled';
import { CtaButton } from './CtaButton';
import { useLocationData } from '@/hooks/useLocationData';
import gpsIconUrl from '@/assets/icons/gps-icon.svg?url';
import eyeIconUrl from '@/assets/icons/eye-icon.svg?url';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  height: 100%;
  overflow: scroll;
`;

const Modal = styled.div`
  background: #ffffff;
  padding: 24px;
  width: 100%;
  max-width: 430px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: scroll;
`;

const Wrapper = styled.div`
  background-color: #f8f9fa;
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 24px;
`;

const Separator = styled.div`
  width: 100%;
  border-bottom: 1px solid lightgray;
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 24px;
`;

const WarningIcon = styled.div`
  width: 60px;
  height: 60px;
  background: #ff4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 40px;
  color: white;
  font-weight: bold;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  color: #212121;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  line-height: 22px;
  color: #666666;
  margin: 0;
`;

const AnalysisSection = styled.div`
  flex: 1;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #999999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 8px 0;
`;

const SectionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-radius: 12px;
  margin-bottom: 12px;
  position: relative;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #e5e7eb;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
`;

const SectionContent = styled.div`
  flex: 1;
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #212121;
  margin-bottom: 4px;
`;

const SectionValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ff4444;
`;

const SectionDescription = styled.div`
  font-size: 10px;
  margin-bottom: 8px;
  color: #666666;
`;

const WarningBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background: ${(props) => props.color || '#ffaa00'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
  font-weight: bold;
`;

const CallToAction = styled.div`
  text-align: center;
`;

const CallToActionText = styled.p`
  font-size: 16px;
  line-height: 22px;
  color: #212121;
  margin: 0 0 24px 0;
`;

const IconImage = styled.img`
  width: 20px;
  height: 20px;
  filter: brightness(0) saturate(100%) invert(47%) sepia(0%) saturate(11%) hue-rotate(348deg) brightness(94%) contrast(89%);
`;

interface SecurityAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SecurityAnalysisModal({ isOpen, onClose }: SecurityAnalysisModalProps) {
  const { locationData, loading } = useLocationData();

  if (!isOpen) return null;

  const analysisData = [
    {
      title: 'CYBERCRIME',
      iconUrl: gpsIconUrl,
      label: 'Your IP',
      value: loading ? 'Loading...' : (locationData?.ip || 'Unknown'),
      description: 'Public Wi-Fi can be a hacker\'s playground—secure your data instantly with AI-driven safeguards.',
      warningColor: '#ffaa00',
    },
    {
      title: 'SPYING',
      iconUrl: gpsIconUrl,
      label: 'Your Location',
      value: loading ? 'Loading...' : (locationData?.city || 'Unknown'),
      description: 'Prevent tracking, surveillance, and unauthorized data collection with intelligent privacy protection.',
      warningColor: '#ff4444',
    },
    {
      title: 'DATA ABUSE',
      iconUrl: eyeIconUrl,
      label: 'Your ISP',
      value: loading ? 'Loading...' : (locationData?.isp || 'Unknown'),
      description: 'Your digital safety is now smarter than ever—adaptive protection based on real-time threats.',
      warningColor: '#ffaa00',
    },
  ];

  return (
    <Overlay>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Wrapper>
          <Header>
            <WarningIcon>!</WarningIcon>
            <Title>Auto Analysis Result</Title>
            <Subtitle>A lot of critically important information about your connection is exposed online!</Subtitle>
          </Header>

          <AnalysisSection>
            {analysisData.map((item, index) => (
              <div key={index}>
                <SectionTitle>{item.title}</SectionTitle>
                <SectionItem>
                  <SectionIcon>
                    <IconImage src={item.iconUrl} alt={item.title} />
                  </SectionIcon>
                  <SectionContent>
                    <SectionLabel>{item.label}</SectionLabel>
                    <SectionValue>{item.value}</SectionValue>
                  </SectionContent>
                  <WarningBadge color={item.warningColor}>!</WarningBadge>
                </SectionItem>
                <SectionDescription>{item.description}</SectionDescription>
                {index !== analysisData.length - 1 && <Separator />}
              </div>
            ))}
          </AnalysisSection>
        </Wrapper>

        <CallToAction>
          <CallToActionText>
            Start browsing securely with confidence. Activate your AI-powered VPN now!
          </CallToActionText>
          <CtaButton onClick={onClose}>
            Continue
          </CtaButton>
        </CallToAction>
      </Modal>
    </Overlay>
  );
}
